import React, { useEffect, useRef, useState } from "react";
import { CalendarCheck, Stethoscope, Sparkles, Pill, QrCode, Cloud } from "lucide-react";
import Reveal from "./Reveal";
import { useLanguage } from "../i18n/LanguageContext";

const ICONS = [CalendarCheck, Stethoscope, Sparkles, Pill, QrCode];

function Visual({ steps, persistent, active }) {
  const step = steps[active];
  const Icon = ICONS[active] || CalendarCheck;
  return (
    <div className="glow rounded-[22px] overflow-hidden border border-white/10 text-white" style={{ background: "linear-gradient(165deg, #163A5C 0%, #0F2A47 65%)", boxShadow: "0 40px 90px -34px rgba(15,42,71,0.5)" }}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
          <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
          <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
        </div>
        <span className="font-mono text-[10px] tracking-[0.16em] text-white/45 uppercase">sajilohealth · app</span>
        <span className="inline-flex items-center gap-1.5">
          <Cloud size={12} strokeWidth={2} className="text-green" />
          <span className="text-[10px] font-semibold text-white/70">Live</span>
        </span>
      </div>
      <div className="p-7 sm:p-9 min-h-[300px] flex flex-col">
        <div key={active} className="scrolly-in flex-1">
          <div className="flex items-center justify-between">
            <span className="inline-flex w-14 h-14 items-center justify-center rounded-2xl bg-white/[0.06] text-green">
              <Icon size={26} strokeWidth={1.6} />
            </span>
            <span className="font-mono text-[12px] text-white/40">
              {String(active + 1).padStart(2, "0")} / {String(steps.length).padStart(2, "0")}
            </span>
          </div>
          <div className="mt-6 inline-flex items-center rounded-md bg-white/[0.08] px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.14em] text-white/60">
            {step.tag}
          </div>
          <h3 className="mt-3 font-display text-[22px] sm:text-[26px] font-semibold tracking-tight leading-[1.2]">{step.title}</h3>
          <p className="mt-3 text-[14.5px] text-white/60 leading-[1.65] max-w-[380px]">{step.cap}</p>
        </div>
        <div className="mt-6 flex items-center gap-1.5">
          {steps.map((_, i) => (
            <span key={i} className={`h-1 rounded-full transition-all duration-300 ${i === active ? "w-7 bg-green" : "w-3 bg-white/15"}`} />
          ))}
        </div>
        <div className="mt-5 pt-4 border-t border-white/10 flex items-center gap-2 text-white/55">
          <Cloud size={13} strokeWidth={1.9} className="text-green" />
          <span className="font-mono text-[11px] tracking-wide">{persistent}</span>
        </div>
      </div>
    </div>
  );
}

export default function Journey() {
  const { t } = useLanguage();
  const j = t.journey;
  const [active, setActive] = useState(0);
  const refs = useRef([]);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(Number(e.target.dataset.index));
        });
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );
    refs.current.forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <section id="journey" data-testid="journey-section" className="py-24 sm:py-32 border-t border-line bg-paper-sunken/50">
      <div className="shell">
        <div className="max-w-[720px]">
          <Reveal className="kicker text-textsoft">{j.kicker}</Reveal>
          <Reveal as="h2" delay={1} className="mt-5 font-display text-[30px] sm:text-[40px] lg:text-[44px] font-semibold text-ink tracking-tight leading-[1.1]">
            {j.h2}
          </Reveal>
          <Reveal as="p" delay={2} className="mt-5 text-[16.5px] text-textsoft leading-[1.6]">{j.sub}</Reveal>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="hidden lg:block lg:col-span-5 lg:sticky lg:top-28 lg:self-start">
            <Visual steps={j.steps} persistent={j.persistent} active={active} />
          </div>

          <div className="lg:col-span-7">
            <ol>
              {j.steps.map((step, i) => {
                const Icon = ICONS[i] || CalendarCheck;
                const on = i === active;
                return (
                  <li key={i} data-index={i} ref={(el) => (refs.current[i] = el)} className="lg:min-h-[60vh] flex lg:items-center">
                    <div className="scrolly-step w-full rounded-2xl border p-6 sm:p-8 border-line bg-white lg:bg-transparent lg:border-transparent">
                      <div className="flex items-center gap-3">
                        <span className={`lg:hidden inline-flex w-11 h-11 items-center justify-center rounded-xl ${on ? "bg-blue-soft text-blue-dark" : "bg-paper-sunken text-textsoft"}`}>
                          <Icon size={19} strokeWidth={1.7} />
                        </span>
                        <span className={`font-mono text-[12px] font-semibold transition-colors ${on ? "text-blue" : "text-textsoft/60"}`}>
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-textsoft/70">{step.tag}</span>
                      </div>
                      <h3 className={`mt-3 font-display text-[22px] sm:text-[28px] font-semibold tracking-tight leading-[1.15] transition-colors ${on ? "text-ink" : "text-ink/55 lg:text-ink/35"}`}>
                        {step.title}
                      </h3>
                      <p className={`mt-3 text-[15px] leading-[1.65] max-w-[460px] transition-colors ${on ? "text-textsoft" : "text-textsoft/60 lg:text-textsoft/35"}`}>
                        {step.cap}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}
